import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Check, Trash, Compass } from "@phosphor-icons/react";
import { bucketApi } from "@/lib/apiClient";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function BucketList() {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [dest, setDest] = useState("");
    const [country, setCountry] = useState("");
    const [note, setNote] = useState("");
    const [imageUrl, setImageUrl] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => bucketApi.list().then(setItems).catch(() => {});
    useEffect(() => { load(); }, []);

    const add = async (e) => {
        e.preventDefault();
        if (!dest || !country) return;
        setSaving(true);
        try {
            await bucketApi.create({ destination: dest, country, note, image_url: imageUrl });
            setDest(""); setCountry(""); setNote(""); setImageUrl(null);
            setShowAdd(false);
            load();
            toast.success(t("bucketList.toasts.added"));
        } catch (e) {
            toast.error(t("bucketList.toasts.couldNotSave"));
        } finally { setSaving(false); }
    };

    const toggle = async (id) => {
        try {
            await bucketApi.toggle(id);
            load();
        } catch (e) {}
    };
    const remove = async (id) => {
        try { await bucketApi.remove(id); load(); } catch (e) {}
    };

    return (
        <div data-testid="bucket-list-panel">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="overline mb-1">{t("bucketList.overline")}</div>
                    <div className="font-serif text-3xl text-white leading-none">
                        {items.filter((i) => i.completed).length} <span className="text-white/40 text-lg">{t("bucketList.crossedOff", { total: items.length })}</span>
                    </div>
                </div>
                <button onClick={() => setShowAdd(true)} className="btn-gold text-sm !py-2.5 !px-5" data-testid="bucket-add">
                    <Plus size={14} weight="bold" /> {t("bucketList.addBtn")}
                </button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((i) => (
                    <motion.div key={i.id} layout
                        className="rounded-2xl overflow-hidden card-hover group"
                        style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}
                        data-testid={`bucket-item-${i.id}`}
                    >
                        <div className="relative aspect-[16/10] overflow-hidden">
                            {i.image_url ? (
                                <img src={i.image_url} alt={i.destination} className="w-full h-full object-cover" style={{ filter: i.completed ? "grayscale(1) brightness(0.6)" : "none" }} />
                            ) : (
                                <div className="w-full h-full grid place-items-center" style={{ background: "linear-gradient(140deg, rgba(212,175,55,0.1), rgba(11,19,32,0.7))" }}>
                                    <Compass size={40} color="#D4AF37" weight="thin" />
                                </div>
                            )}
                            {i.completed && (
                                <div className="absolute inset-0 grid place-items-center">
                                    <div className="w-14 h-14 rounded-full grid place-items-center" style={{ background: "var(--gold)" }}>
                                        <Check size={22} weight="bold" color="#0B1320" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="p-4">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{i.country}</div>
                            <div className="font-serif text-lg text-white leading-tight mt-1 mb-3">{i.destination}</div>
                            <div className="flex gap-2">
                                <button onClick={() => toggle(i.id)} className="flex-1 py-2 rounded-full text-xs transition-colors"
                                    style={{ background: i.completed ? "transparent" : "rgba(212,175,55,0.1)", color: i.completed ? "rgba(255,255,255,0.7)" : "var(--gold)", border: "1px solid " + (i.completed ? "rgba(255,255,255,0.1)" : "rgba(212,175,55,0.3)") }}
                                    data-testid={`bucket-toggle-${i.id}`}
                                >
                                    {i.completed ? t("bucketList.undo") : t("bucketList.markDone")}
                                </button>
                                <button onClick={() => remove(i.id)} className="w-9 h-9 rounded-full grid place-items-center hover:bg-white/5 transition-colors" data-testid={`bucket-delete-${i.id}`} aria-label="Delete">
                                    <Trash size={12} color="rgba(255,255,255,0.5)" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                ))}
                {items.length === 0 && (
                    <div className="col-span-full text-center py-16 rounded-2xl" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                        <div className="text-white/60">{t("bucketList.empty")}</div>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {showAdd && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                        style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }}
                        onClick={() => setShowAdd(false)}
                    >
                        <motion.form
                            onClick={(e) => e.stopPropagation()}
                            onSubmit={add}
                            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                            className="rounded-3xl max-w-lg w-full p-8"
                            style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}
                            data-testid="bucket-add-modal"
                        >
                            <div className="overline mb-2">{t("bucketList.modal.overline")}</div>
                            <h3 className="font-serif text-3xl text-white mb-6">{t("bucketList.modal.title")}</h3>
                            <div className="space-y-4">
                                <div>
                                    <span className="overline block mb-2">{t("bucketList.modal.destinationLabel")}</span>
                                    <input required value={dest} onChange={(e) => setDest(e.target.value)} placeholder={t("bucketList.modal.destinationPlaceholder")} className="input" data-testid="bucket-dest" />
                                </div>
                                <div>
                                    <span className="overline block mb-2">{t("bucketList.modal.countryLabel")}</span>
                                    <input required value={country} onChange={(e) => setCountry(e.target.value)} placeholder={t("bucketList.modal.countryPlaceholder")} className="input" data-testid="bucket-country" />
                                </div>
                                <div>
                                    <span className="overline block mb-2">{t("bucketList.modal.noteLabel")}</span>
                                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("bucketList.modal.notePlaceholder")} className="input" data-testid="bucket-note" />
                                </div>
                                <ImageUploader value={imageUrl} onChange={setImageUrl} label={t("bucketList.modal.coverImageLabel")} testId="bucket-image" height={140} />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost flex-1 justify-center" data-testid="bucket-cancel">{t("bucketList.modal.cancel")}</button>
                                <button type="submit" disabled={saving} className="btn-gold flex-1 justify-center" data-testid="bucket-save">
                                    {saving ? "…" : t("bucketList.modal.save")}
                                </button>
                            </div>
                            <style>{`
                                .input { width: 100%; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 12px 14px; color: white; font-size: 14px; transition: border-color 0.3s ease; }
                                .input::placeholder { color: rgba(255,255,255,0.35); }
                                .input:focus { outline: none; border-color: var(--gold); }
                            `}</style>
                        </motion.form>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
